import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Calculator, DollarSign, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RateCalculator = () => {
  const [formData, setFormData] = useState({
    distance_miles: '',
    weight_lbs: '',
    is_hazmat: false,
    is_expedited: false,
    detention_hours: 0,
    layover_days: 0,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCalculate = async () => {
    if (!formData.distance_miles || !formData.weight_lbs) {
      toast.error('Please enter distance and weight');
      return;
    }

    try {
      setLoading(true);
      const params = new URLSearchParams({
        distance_miles: parseFloat(formData.distance_miles),
        weight_lbs: parseFloat(formData.weight_lbs),
        is_hazmat: formData.is_hazmat,
        is_expedited: formData.is_expedited,
        detention_hours: parseInt(formData.detention_hours) || 0,
        layover_days: parseInt(formData.layover_days) || 0,
      });
      const response = await axios.post(`${API}/calculate-rate?${params}`);
      setResult(response.data);
      toast.success('Rate calculated successfully');
    } catch (error) {
      console.error('Error calculating rate:', error);
      toast.error('Failed to calculate rate');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="glass-card" data-testid="rate-calculator-card">
      <CardHeader>
        <CardTitle className="text-xl font-heading flex items-center gap-2">
          <Calculator className="w-5 h-5 text-primary" />
          Smart Rate Calculator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Distance (miles)</Label>
            <Input
              type="number"
              placeholder="2500"
              value={formData.distance_miles}
              onChange={(e) => setFormData({...formData, distance_miles: e.target.value})}
              data-testid="input-distance"
            />
          </div>
          <div>
            <Label>Weight (lbs)</Label>
            <Input
              type="number"
              placeholder="25000"
              value={formData.weight_lbs}
              onChange={(e) => setFormData({...formData, weight_lbs: e.target.value})}
              data-testid="input-weight"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Detention Hours</Label>
            <Input
              type="number"
              placeholder="0"
              value={formData.detention_hours}
              onChange={(e) => setFormData({...formData, detention_hours: e.target.value})}
            />
          </div>
          <div>
            <Label>Layover Days</Label>
            <Input
              type="number"
              placeholder="0"
              value={formData.layover_days}
              onChange={(e) => setFormData({...formData, layover_days: e.target.value})}
            />
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 rounded-lg bg-background/50">
            <Label>Hazmat Load</Label>
            <Switch
              checked={formData.is_hazmat}
              onCheckedChange={(checked) => setFormData({...formData, is_hazmat: checked})}
            />
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-background/50">
            <Label>Expedited Service</Label>
            <Switch
              checked={formData.is_expedited}
              onCheckedChange={(checked) => setFormData({...formData, is_expedited: checked})}
            />
          </div>
        </div>

        <Button 
          onClick={handleCalculate} 
          className="w-full glow-button"
          disabled={loading}
          data-testid="calculate-rate-button"
        >
          {loading ? 'Calculating...' : 'Calculate Recommended Rate'}
        </Button>

        {result && (
          <div className="mt-6 space-y-3">
            <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Recommended Total Rate</span>
                <DollarSign className="w-5 h-5 text-primary" />
              </div>
              <p className="text-3xl font-black font-heading text-primary">
                ${result.total_rate.toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                ${result.rate_per_mile}/mile • {result.weight_class} class
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-background/50">
                <p className="text-xs text-muted-foreground">Base Rate</p>
                <p className="text-lg font-mono font-semibold text-foreground">
                  ${result.base_rate.toLocaleString()}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-background/50">
                <p className="text-xs text-muted-foreground">Fuel Surcharge</p>
                <p className="text-lg font-mono font-semibold text-foreground">
                  ${result.fuel_surcharge.toLocaleString()}
                </p>
              </div>
            </div>

            {result.accessorial_charges > 0 && (
              <div className="p-3 rounded-lg bg-background/50">
                <p className="text-xs text-muted-foreground mb-2">Accessorial Charges</p>
                {result.accessorial_breakdown.map(([name, amount], idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm mb-1">
                    <span className="text-foreground">{name}</span>
                    <span className="font-mono font-semibold text-foreground">
                      ${amount.toLocaleString()}
                    </span>
                  </div>
                ))}
                <div className="border-t border-border/50 mt-2 pt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground">Total Accessorial</span>
                    <span className="font-mono font-bold text-primary">
                      ${result.accessorial_charges.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                <p className="text-xs text-emerald-500">
                  This rate includes 18% fuel surcharge and industry-standard pricing
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RateCalculator;
